import moment from 'moment';

import { isTimestampLessThanOneWeek, isTimestampSameYear, isTimestampToday, isTimestampTomorrow } from '../utils/date';

import { __ } from './l10n';
import NumberFormatter, { type NumberFormatterConfigurationType } from './numberformatter';

type DateConfigurationType = {
  months: {
    full: Array<string>;
    abbr: Array<string>;
  };
  days: {
    full: Array<string>;
    abbr: Array<string>;
  };
  patterns: {
    longDate: string;
    dateSlash: string;
    dateShort: string;
    dateShortWithDay: string;
    dateMedium: string;
    dateMediumReversed: string;
    dateMediumSlash: string;
    dateLarge: string;
    timeShort: string;
    timeMedium: string;
    timeLarge: string;
  };
};

interface IFormatter {
  setConfiguration: (dateConf: DateConfigurationType, numberConfiguration: NumberFormatterConfigurationType) => void;
  formatOdd: (value: number) => string;
  formatPercent: (value: number) => string;
  formatBankroll: (value: number, currency: string, removeDecimals?: boolean) => string;
  formatNumber: (
    value: number,
    currency: string,
    abbreviate?: boolean,
    trimDecimalPartWhenPossible?: boolean,
    removeDecimals?: boolean,
  ) => string;
  abbreviateNumber: (value: number, currency: string, decimalLength: number) => string;
  formatCount: (value: number) => string;
  formatOrdinal: (value: number) => string;
  formatDate: (date: number, format: string) => string;
  formatCalendar: (date: number) => string;
  formatMatchStart: (matchStart: number) => string;
}

class Formatter implements IFormatter {
  private dateConfiguration: DateConfigurationType | null = null;

  setConfiguration = (dateConf: DateConfigurationType, numberConfiguration: NumberFormatterConfigurationType) => {
    NumberFormatter.setConfiguration(numberConfiguration);
    this.dateConfiguration = dateConf;
  };

  formatOdd = (value: number) => {
    return NumberFormatter.formatOdd(value);
  };

  formatPercent = (value: number) => {
    if (value < 0 || value > 100) {
      console.error(`Invalid percent value "${value}". Value must be between 0 and 100`);
    }
    return NumberFormatter.format(value, '%');
  };

  formatBankroll = (value: number, currency: string, removeDecimals = false) => {
    /* On divise systématiquement la value par 100 puisqu'elle est fournie en centimes depuis le store */
    const v = value / 100;
    return NumberFormatter.format(
      v > 10000 ? Math.floor(v) : v,
      currency,
      false,
      removeDecimals ? 0 : undefined,
      v > 10000,
    );
  };

  formatNumber = (
    value: number,
    currency: string,
    abbreviate = false,
    trimDecimalPartWhenPossible = false,
    removeDecimals = false,
  ) => {
    return NumberFormatter.format(
      value,
      currency,
      abbreviate,
      removeDecimals ? 0 : undefined,
      trimDecimalPartWhenPossible,
    );
  };

  abbreviateNumber = (value: number, currency: string, decimalLength: number) => {
    return NumberFormatter.abbreviate(value, currency, decimalLength);
  };

  formatCount = (value: number) => {
    return NumberFormatter.format(value, '', false, 0);
  };

  formatOrdinal = (value: number) => {
    return NumberFormatter.ordinal(value);
  };

  formatDate = (date: number, format: string) => {
    let f = format;

    // On remplace le format par un format de la config si c'est un alias
    if (this.dateConfiguration) {
      const isKnownPatternFormat =
        format === 'longDate' ||
        format === 'dateSlash' ||
        format === 'dateShort' ||
        format === 'dateShortWithDay' ||
        format === 'dateMedium' ||
        format === 'dateMediumReversed' ||
        format === 'dateMediumSlash' ||
        format === 'dateLarge' ||
        format === 'timeShort' ||
        format === 'timeMedium' ||
        format === 'timeLarge';

      if (this.dateConfiguration.patterns && isKnownPatternFormat) {
        f = this.dateConfiguration.patterns[format];
      }
    }
    return moment.unix(date).format(f);
  };

  formatCalendar = (date: number) => {
    return moment.unix(date).calendar();
  };

  formatMatchStart = (matchStart: number) => {
    // Avant ce soir 00h00
    if (isTimestampToday(matchStart)) {
      return __('!short:Today') + ' ' + this.formatDate(matchStart, 'timeShort');
    }
    // Avant demain 00h00
    if (isTimestampTomorrow(matchStart)) {
      return __('Tomorrow') + ' ' + this.formatDate(matchStart, 'timeShort');
    }
    // Avant J+7
    if (isTimestampLessThanOneWeek(matchStart)) {
      return this.formatDate(matchStart, 'dddd LT');
    }

    // Après J+7 dans la même année
    if (isTimestampSameYear(matchStart)) {
      return this.formatDate(matchStart, 'timeMedium');
    }

    // Après J+7 année différente
    return this.formatDate(matchStart, 'timeLarge');
  };
}

const FormatterInstance = new Formatter();

export default FormatterInstance;
